import { Popover, Box, BlockStack, Text, Icon, InlineStack } from '@shopify/polaris';
import { GActivatorPopover } from '../GActivatorPopover';
import { CheckIcon } from '@shopify/polaris-icons';
import { useState, useMemo } from 'react';

export interface GViewBySelectorOption {
  id: string;
  title?: string;
  content: string;
  helpText: string;
  icon: string;
}

export interface GViewBySelectorProps {
  minWidth?: string;
  maxWidth?: string;
  activatorText: string;
  options: GViewBySelectorOption[];
  selected?: string;
  onSelect: (value: string) => void;
  preferredAlignment?: 'left' | 'right';
}

export const GViewBySelector: React.FC<GViewBySelectorProps> = ({
  activatorText,
  selected,
  options,
  minWidth,
  maxWidth,
  preferredAlignment = 'right',
  onSelect,
}) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const [itemHover, setItemHover] = useState<string | null>(null);

  const togglePopoverActive = () => setPopoverActive((prev) => !prev);

  const selectedOption = useMemo(() => {
    return options.find((item) => item.id === selected);
  }, [options, selected]);

  const activatorContent = useMemo(() => {
    if (selectedOption) {
      const content = selectedOption.title ?? selectedOption.content;
      return activatorText ? `${activatorText}: ${content}` : content;
    }
    return activatorText;
  }, [selectedOption, activatorText]);

  const handleSelect = (value: string) => {
    onSelect(value);
    setPopoverActive(false);
  };

  return (
    <Popover
      active={popoverActive}
      activator={
        <GActivatorPopover
          active={popoverActive}
          content={activatorContent}
          textProps={{ variant: 'bodySm' }}
          onClick={togglePopoverActive}
        />
      }
      autofocusTarget="first-node"
      fluidContent
      onClose={togglePopoverActive}
      preferredAlignment={preferredAlignment}
    >
      <Box maxWidth={maxWidth} minWidth={minWidth} padding="200">
        <BlockStack gap="200">
          {options.map((option) => {
            const isSelected = option.id === selected;
            const isHover = itemHover === option.id;
            const borderColor = isSelected
              ? 'input-border'
              : isHover
                ? 'border-hover'
                : 'transparent';

            return (
              <Box
                key={option.id}
                padding="150"
                borderWidth="025"
                borderColor={borderColor}
                borderRadius="100"
              >
                <div
                  onClick={() => handleSelect(option.id)}
                  className="cursor-pointer"
                  onMouseEnter={() => setItemHover(option.id)}
                  onMouseLeave={() => setItemHover(null)}
                >
                  <InlineStack gap="300" align="space-between" blockAlign="center" wrap={false}>
                    <InlineStack gap="300" blockAlign="center" wrap={false}>
                      <div className="bg-surface-secondary flex items-center justify-center rounded-md min-w-[184px] h-[92px]">
                        <img
                          src={option.icon}
                          alt={option.content}
                          className="w-full h-full object-contain"
                        />
                      </div>
                      <BlockStack gap="100">
                        <Text as="span" variant="bodyMd" fontWeight="semibold">
                          {option.content}
                        </Text>
                        <Text as="p" variant="bodyMd" tone="subdued">
                          {option.helpText}
                        </Text>
                      </BlockStack>
                    </InlineStack>
                    {isSelected && (
                      <Box>
                        <Icon source={CheckIcon} />
                      </Box>
                    )}
                  </InlineStack>
                </div>
              </Box>
            );
          })}
        </BlockStack>
      </Box>
    </Popover>
  );
};
